import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, ChevronRight, Info, RotateCcw } from "lucide-react";
import { Overline, Btn } from "../components/ui-vision";
import { useLang } from "../lib/i18n";
import { useStepScroll } from "../lib/useStepScroll";
import { TREE, START, pick, PRIMES_UI } from "../lib/primesData";

const spring = { type: "spring", stiffness: 260, damping: 30 };

const OptionCard = ({ label, sub, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="group w-full text-left rounded-2xl border border-white/12 bg-white/[0.03] hover:border-accent-yellow/60 hover:bg-white/[0.06] focus:border-accent-yellow focus:outline-none px-5 py-5 md:px-6 flex items-center justify-between gap-4 transition-colors duration-300"
  >
    <span className="min-w-0">
      <span className="block text-base text-white leading-snug">{label}</span>
      {sub && <span className="mt-1 block text-sm text-white/45">{sub}</span>}
    </span>
    <ChevronRight
      size={20}
      className="shrink-0 text-white/25 group-hover:text-accent-yellow group-hover:translate-x-1 transition-all duration-300"
    />
  </button>
);

const StepHeading = ({ children }) => (
  <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight leading-[1.1]">{children}</h2>
);

const QuestionStep = ({ node, goTo, lang }) => (
  <div>
    <StepHeading>{pick(node.question, lang)}</StepHeading>
    <div className="mt-8 grid gap-3 md:gap-4 max-w-2xl">
      {node.options.map((o) => (
        <OptionCard key={o.to} label={pick(o.label, lang)} onClick={() => goTo(o.to)} testid={`prime-option-${o.to}`} />
      ))}
    </div>
  </div>
);

const IncomeFlandre = ({ node, goTo, lang }) => {
  const [sit, setSit] = useState(node.situations[0].key);
  const active = node.situations.find((s) => s.key === sit);
  return (
    <div>
      <StepHeading>{pick(node.question, lang)}</StepHeading>
      {node.help && <p className="mt-4 text-white/55 font-light max-w-xl">{pick(node.help, lang)}</p>}

      <div className="mt-7 inline-flex flex-wrap gap-2 rounded-2xl border border-white/12 bg-white/[0.02] p-1.5">
        {node.situations.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setSit(s.key)}
            data-testid={`prime-fl-sit-${s.key}`}
            className={`rounded-xl px-4 py-2.5 text-sm transition-colors duration-300 ${
              sit === s.key ? "bg-accent-yellow text-ink font-semibold" : "text-white/70 hover:text-white"
            }`}
          >
            {pick(s.label, lang)}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-3 md:gap-4 max-w-2xl">
        {active.rows.map((row) => (
          <OptionCard
            key={row.to}
            label={pick(row.cat, lang)}
            sub={pick(row.threshold, lang)}
            onClick={() => goTo(row.to)}
            testid={`prime-option-${row.to}`}
          />
        ))}
      </div>

      {node.dependentNote && (
        <p className="mt-5 max-w-2xl text-sm text-white/45 flex items-start gap-2">
          <Info size={15} className="mt-0.5 shrink-0 text-accent-yellow" />
          {pick(node.dependentNote, lang)}
        </p>
      )}
    </div>
  );
};

const IncomeWallonie = ({ node, goTo, lang }) => (
  <div>
    <StepHeading>{pick(node.question, lang)}</StepHeading>
    {node.help && <p className="mt-4 text-white/55 font-light max-w-xl">{pick(node.help, lang)}</p>}
    <div className="mt-7 grid gap-3 md:gap-4 max-w-2xl">
      {node.rows.map((row) => (
        <OptionCard
          key={row.to}
          label={pick(row.cat, lang)}
          sub={pick(row.range, lang)}
          onClick={() => goTo(row.to)}
          testid={`prime-option-${row.to}`}
        />
      ))}
    </div>
  </div>
);

const ResultStep = ({ node, onModify, onRestart, lang }) => {
  const ui = (k) => PRIMES_UI[k][lang];
  const showPrime = node.prime && (node.eligible || node.tone === "info");
  return (
    <div
      className="rounded-3xl border border-white/12 bg-white/[0.03] p-7 md:p-12"
      data-testid="prime-result"
      data-eligible={node.eligible ? "true" : "false"}
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <span
          className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs uppercase tracking-[0.2em] ${
            node.eligible
              ? "border-accent-yellow/50 text-accent-yellow bg-accent-yellow/10"
              : "border-white/15 text-white/55 bg-white/[0.03]"
          }`}
          data-testid="prime-result-status"
        >
          {node.eligible ? <Check size={14} /> : <Info size={14} />}
          {node.eligible ? ui("eligible") : ui("notEligible")}
        </span>
        <Overline>{ui("result")}</Overline>
      </div>

      {showPrime && (
        <div className="mt-7">
          <p className="text-xs uppercase tracking-[0.25em] text-white/40 mb-2">{ui("prime")}</p>
          <h3 className="font-display text-3xl md:text-4xl font-medium tracking-tight text-accent-yellow">
            {pick(node.prime, lang)}
          </h3>
          {node.category && <p className="mt-2 text-sm text-white/45">{pick(node.category, lang)}</p>}
        </div>
      )}

      <p className="mt-6 text-lg text-white/75 font-light leading-relaxed max-w-2xl">{pick(node.intro, lang)}</p>

      {node.metrics && (
        <div className="mt-9 grid sm:grid-cols-2 gap-px rounded-2xl overflow-hidden border border-white/10 bg-white/10">
          {node.metrics.map((m, i) => {
            const spanFull = node.metrics.length % 2 === 1 && i === node.metrics.length - 1;
            return (
              <div key={pick(m.label, lang)} className={`bg-ink-2 p-6 ${spanFull ? "sm:col-span-2" : ""}`} data-testid="prime-metric">
                <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3">{pick(m.label, lang)}</p>
                <p className="font-display text-4xl md:text-5xl font-medium text-accent-yellow leading-none">
                  {pick(m.value, lang)}
                </p>
                {m.note && <p className="mt-2 text-sm text-white/45">{pick(m.note, lang)}</p>}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 flex items-center gap-4 border-t border-white/10 pt-6">
        <p className="text-xs uppercase tracking-[0.25em] text-white/40">{pick(node.tva.label, lang)}</p>
        <span className="font-display text-2xl font-medium text-white" data-testid="prime-tva">
          {pick(node.tva.value, lang)}
        </span>
      </div>

      {node.conditions && (
        <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8">
          <p className="text-xs uppercase tracking-[0.25em] text-accent-yellow mb-5">{ui("conditionsTitle")}</p>
          <p className="text-sm text-white/55 font-light mb-5 max-w-2xl">{ui("conditionsIntro")}</p>
          <ul className="space-y-3">
            {node.conditions.map((c, i) => (
              <li key={i} className="flex gap-3 text-sm text-white/65 leading-relaxed">
                <span className="text-accent-yellow shrink-0 mt-1">—</span>
                <span>{pick(c, lang)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
        <button
          type="button"
          onClick={onModify}
          data-testid="prime-modify"
          className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-accent-yellow transition-colors duration-300"
        >
          <ArrowLeft size={16} /> {ui("modify")}
        </button>
        <button
          type="button"
          onClick={onRestart}
          data-testid="prime-restart"
          className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-accent-yellow transition-colors duration-300"
        >
          <RotateCcw size={15} /> {ui("restart")}
        </button>
        {node.eligible && (
          <div className="ml-auto">
            <Btn to="/rendez-vous" variant="solid" testid="prime-rdv-cta">
              {ui("rdvCta")}
            </Btn>
          </div>
        )}
      </div>
    </div>
  );
};

export default function Primes() {
  const { lang } = useLang();
  const [path, setPath] = useState([START]);
  const currentId = path[path.length - 1];
  const node = TREE[currentId];
  const ui = (k) => PRIMES_UI[k][lang];
  const formRef = useRef(null);
  useStepScroll(currentId, formRef);

  const goTo = (id) => setPath((p) => [...p, id]);
  const back = () => setPath((p) => (p.length > 1 ? p.slice(0, -1) : p));
  const reset = () => setPath([START]);

  const isResult = node.type === "result";
  const canGoBack = path.length > 1;

  return (
    <section className="pt-28 md:pt-32 pb-16 md:pb-20 max-w-[1440px] mx-auto px-6 md:px-12" data-testid="primes-simulator">
      <div className="max-w-3xl mx-auto space-y-6 lg:space-y-10">
        <div>
          <div className="lg:hidden rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4" data-testid="primes-mobile-title">
            <Overline>{ui("mobileTitle")}</Overline>
          </div>
          <div className="hidden lg:block rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4" data-testid="primes-intro-card">
            <Overline>{ui("mobileTitle")}</Overline>
          </div>
        </div>

        <div ref={formRef}>
          <div className="rounded-3xl border border-white/10 bg-white/[0.015] p-6 md:p-10 lg:p-12">
        {!isResult && (
          <div className="flex items-center justify-between gap-4 mb-8 min-h-[24px]">
            {canGoBack ? (
              <button
                type="button"
                onClick={back}
                data-testid="prime-back"
                className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-accent-yellow transition-colors duration-300"
              >
                <ArrowLeft size={16} /> {ui("back")}
              </button>
            ) : (
              <span className="text-xs uppercase tracking-[0.25em] text-white/35">{ui("simulatorLabel")}</span>
            )}
            {canGoBack && (
              <button
                type="button"
                onClick={reset}
                data-testid="prime-reset"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40 hover:text-white transition-colors duration-300"
              >
                <RotateCcw size={13} /> {ui("restartShort")}
              </button>
            )}
          </div>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={currentId}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={spring}
          >
            {node.type === "question" && <QuestionStep node={node} goTo={goTo} lang={lang} />}
            {node.type === "income_fl" && <IncomeFlandre node={node} goTo={goTo} lang={lang} />}
            {node.type === "income_wal" && <IncomeWallonie node={node} goTo={goTo} lang={lang} />}
            {node.type === "result" && <ResultStep node={node} onModify={back} onRestart={reset} lang={lang} />}
          </motion.div>
        </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
